import { type PropsWithChildren, type JSX } from 'react';
import { useRouteChangeComplete } from '../../../hooks/useAppRouter';
import { MAIN_CONTENT_LANDMARK_ID } from '../../../shared/constants/mainContentLandmark';

/**
 * Root app shell wrapper (`div.app`) for the standard (non-iframe) tree. After
 * client-side navigations, moves focus to the in-page primary region
 * (`#main-content`) when present so keyboard users land past global chrome.
 */
export const AppMain = ({ children }: PropsWithChildren): JSX.Element => {
  useRouteChangeComplete(() => {
    requestAnimationFrame(() => {
      const primary = document.getElementById(MAIN_CONTENT_LANDMARK_ID);
      primary?.focus({ preventScroll: true });
    });
  });

  return (
    <div
      className='app'
      key='app'
      tabIndex={-1}
      css={{
        height: 'inherit',
        width: 'inherit',
        // Focus moves here after navigation for SR/landmark semantics, not as a visible
        // control. Suppress the UA ring for both programmatic :focus and keyboard
        // :focus-visible so the entire viewport is not outlined.
        '&:focus, &:focus-visible': {
          outline: 'none',
        },
      }}
    >
      {children}
    </div>
  );
};
